import { useLocalization, LabeledGroup, IconCheck, Select } from '@magicdoorinc/ui';
import { createMemo, For, Show, splitProps } from 'solid-js';
import { useChartOfAccounts } from '~/contexts/global';
import { formatKnownCoaLabel } from '~/utils/chartOfAccountLabel';
import type { LabeledGroupProps, SelectProps } from '@magicdoorinc/ui';
import type { Accessor, JSX } from 'solid-js';
import type { ChartOfAccountGroup } from '~/contexts/global';
import type { ChartOfAccountType } from '~/swagger/portal/Api';
import type { Merge } from '~/utils/types';

type AccountType = `${ChartOfAccountType}`;

type ChartOfAccountSelectProps<T extends boolean | undefined> = Merge<
  SelectProps<string, T>,
  {
    options?: never;
    types?: AccountType | AccountType[];
    withAll?: boolean;
    allowedIds?: string[];
    chartOfAccountGroups?: Accessor<ChartOfAccountGroup[]>;
  }
>;

type LeveledOption = { label: string; value: string; level: number; parentLast?: boolean; selfLast?: boolean };

const Spacer = (props: JSX.SvgSVGAttributes<SVGSVGElement>) => (
  <svg class="h-10 shrink-0 stroke-border" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 40" stroke-width="1.5" fill="none" {...props}>
    <path d="M30 20h-7.5s-7.5.596-7.5-7.5V0v40" />
  </svg>
);

const LevelSpacer = (props: LeveledOption) => (
  <Show when={props.level > 0}>
    <Show when={!props.parentLast} fallback={<i style={{ width: `${(props.level - 1) * 30}px` }} />}>
      <For each={Array(props.level - 1)}>{() => <Spacer stroke-dasharray="40 100" stroke-dashoffset="-32" />}</For>
    </Show>
    <Spacer stroke-dasharray={props.selfLast ? '40 100' : undefined} />
  </Show>
);

export const ChartOfAccountSelect = <T extends boolean | undefined = undefined>(props: ChartOfAccountSelectProps<T>) => {
  const { t } = useLocalization();

  const chartOfAccountGroups = createMemo(() => props.chartOfAccountGroups?.() || useChartOfAccounts().chartOfAccountGroups());

  const filtered = () => {
    const types = props.types;
    const groups = chartOfAccountGroups();
    if (!groups) return [];

    const filterByTypes = (input: typeof groups) => {
      if (!types) return input;
      if (Array.isArray(types)) {
        return input.filter((group) => types.includes(group.type));
      }
      return input.filter((group) => group.type === types);
    };

    const filterByAllowedIds = (input: typeof groups) => {
      if (!props.allowedIds || props.allowedIds.length === 0) return input;
      const allowedSet = new Set(props.allowedIds);

      const filterItems = (items: ChartOfAccountGroup['items']): ChartOfAccountGroup['items'] => {
        if (!items) return [];
        const result: ChartOfAccountGroup['items'] = [];
        items.forEach((item) => {
          const filteredChildren = filterItems(item.children || []);
          const isAllowed = allowedSet.has(item.id);
          if (isAllowed) {
            result.push({ ...item, children: filteredChildren });
          } else if (filteredChildren.length) {
            result.push(...filteredChildren);
          }
        });
        return result;
      };

      return input
        .map((group) => {
          const filteredItems = filterItems(group.items);
          return { ...group, items: filteredItems };
        })
        .filter((group) => group.items.length > 0);
    };

    return filterByAllowedIds(filterByTypes(groups));
  };

  const loadItems = (items: ChartOfAccountGroup['items'], level = 0, parentLast = false): LeveledOption[] =>
    items?.flatMap((item, i) => [
      { label: formatKnownCoaLabel(item, t), value: item?.id || '', level, parentLast, selfLast: i === items.length - 1 },
      ...loadItems(item?.children || [], level + 1, i === items.length - 1),
    ]) || [];

  const options = () => {
    const groups = filtered();
    if (!groups || groups.length === 0) return [];
    return groups.flatMap((group) => [{ label: group.title, type: 'group' as const }, ...loadItems(group.items)]);
  };

  const allOptions = () => {
    const inner = options();
    return props.withAll ? [{ label: t('All'), value: '' }, ...inner] : inner;
  };

  return (
    <Select
      {...props}
      popoverClass="gap-0"
      options={allOptions()}
      placeholder={props.placeholder ?? t('Please type or select a chart of account')}
      filterable={props.filterable ?? true}
      renderOption={(option) => (
        <div class="flex items-center">
          <LevelSpacer {...(option as unknown as LeveledOption)} />
          <span class="flex flex-1 cursor-pointer items-center rounded-md px-3 py-2.5 transition-colors">
            {option.label}
            <Show when={option.selected}>
              <IconCheck class="ml-auto size-4 text-primary" />
            </Show>
          </span>
        </div>
      )}
    />
  );
};

export const LabeledChartOfAccountSelect = <T extends boolean | undefined = undefined>(
  props: Omit<ChartOfAccountSelectProps<T>, 'onChange'> &
    LabeledGroupProps & { onInput?: ChartOfAccountSelectProps<T>['onChange'] } & {
      groupClass?: string;
    }
) => {
  const [selectProps, labeledProps] = splitProps(props, [
    'renderOption',
    'prefix',
    'value',
    'multiple',
    'filterable',
    'placeholder',
    'types',
    'withAll',
    'allowedIds',
    'onInput',
    'class',
    'chartOfAccountGroups',
  ]);

  return (
    <LabeledGroup {...labeledProps}>
      <ChartOfAccountSelect {...selectProps} id={labeledProps.id} onChange={props.onInput} filterable={props.filterable ?? true} />
    </LabeledGroup>
  );
};
